<script>
  import { onMount, tick, afterUpdate } from 'svelte';
  import { fade } from 'svelte/transition';
  import baguetteBox from 'baguettebox.js';
  
  export let data = [];

  let selectedStyle = '';
  // let selectedColor = '';
  let selectedRoom = '';
  let selectedLevel = '';
  let selectedSize = '';
  let selectedProject = '';

function openLightbox(event, img) {
  // No-op since BaguetteBox will use anchor href
}

function clearFilters() {
  selectedStyle = '';
  // selectedColor = '';
  selectedRoom = '';
  selectedProject = '';
  selectedLevel = '';
  selectedSize = '';
}


  // derive filter options
  function normalize(val) {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(v => (typeof v === 'object' && v !== null && v.label ? String(v.label) : String(v)));
    if (val instanceof Set) return Array.from(val).map(v => (typeof v === 'object' && v !== null && v.label ? String(v.label) : String(v)));
if (typeof val === 'object' && val !== null) {
  if ('label' in val) return [val.label];
  if (val instanceof Set) return Array.from(val);
  return Object.values(val).map(v => typeof v === 'object' && v.label ? v.label : v);
}
    return Array.isArray(val) ? val.map(String) : [String(val)];
  }

  // filter options, initialized empty, assigned in onMount
  let styleOptions = [];
  // let colorOptions = [];
  let roomOptions = [];
  let projectOptions = [];
  let levelOptions = [];
  let sizeOptions = [];

  // Helper to convert possible value types to array of strings
  function toStrings(arr) {
    return arr.flatMap(item => {
      const norm = normalize(item) || [];
      if (norm instanceof Set) {
        return Array.from(norm).map(String);
      }
      if (Array.isArray(norm)) {
        return norm.map(String);
      }
      return [String(norm)];
    });
  }

  // Build filter options from data
  function buildOptions() {
    styleOptions = Array.from(new Set(toStrings(data.map(item => item.style))))
      .filter(Boolean)
      .map(v => ({ value: v, label: v }));

    // colorOptions = Array.from(new Set(toStrings(data.map(item => item.color))))
    //   .filter(Boolean)
    //   .map(v => ({ value: v, label: v }));

    roomOptions = Array.from(new Set(toStrings(data.map(item => item.room))))
      .filter(v => v && v !== "None" && v !== "none")
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ value: v, label: v }));

    projectOptions = Array.from(new Set(toStrings(data.map(item => item.project))))
      .filter(Boolean)
      .map(v => ({ value: v, label: v }));

    levelOptions = Array.from(new Set(data.map(item => item.levels && item.levels.label).filter(Boolean)))
      .map(v => ({ value: v, label: v }));

    sizeOptions = Array.from(new Set(data.map(item => item.size && item.size.label).filter(Boolean)))
      .map(v => ({ value: v, label: v }));
  }

  onMount(() => {
    baguetteBox.run('.baguette-gallery', {
      captions: true,
      buttons: 'auto',
    });
  });

  // Reactive: update options when data changes
  $: if (data && data.length) buildOptions();
  $: if (!data || data.length === 0) {
    styleOptions = [];
    roomOptions = [];
    projectOptions = [];
    levelOptions = [];
    sizeOptions = [];
  }

  // reactive filtered images
  $: filteredImages = data.filter(img => {
    if (img.room === "None" || img.room === "none") return false;
    const matchStyle = !selectedStyle || (img.style && img.style.includes(selectedStyle));
    // const matchColor = !selectedColor || (img.color && img.color.includes(selectedColor));
    const matchRoom = !selectedRoom || img.room === selectedRoom;
    const matchProject = !selectedProject || img.project === selectedProject;
    const matchLevel = !selectedLevel || (img.levels && img.levels.label === selectedLevel);
    const matchSize = !selectedSize || (img.size && img.size.label === selectedSize);
    return matchStyle && matchRoom && matchProject && matchLevel && matchSize;
  });
  

$: allImages = filteredImages
  .map(img => {
    const validFull = (typeof img.full === 'string' && img.full.trim() && img.full !== 'none') ? img.full.trim() : null;
    const validOriginal = (typeof img.original === 'string' && img.original.trim() && img.original !== 'none') ? img.original.trim() : null;
    const validSrc = (typeof img.src === 'string' && img.src.trim() && img.src !== 'none') ? img.src.trim() : null;
    const imageUrl = validFull || validOriginal || validSrc;

    if (!imageUrl) {
      console.warn("Skipping image with no valid URL:", img);
      return null;
    }

    return {
      src: imageUrl,
      large: imageUrl,
      alt: img.room ? `${img.room} – ${img.project}` : img.project,
      caption: img.project,
      link: imageUrl, // for lightbox
      projectLink: img.link // backend project page
    };
  })
  .filter(Boolean);

let observer;

async function initLazyLoad() {
  try {
    await tick();

    if (observer) observer.disconnect();

    const container = document.querySelector('.sub-links-wrap');
    if (!container) {
      console.warn('Lazy load container ".sub-links-wrap" not found.');
      return;
    }

    const elements = container.querySelectorAll('.gal-item-background[data-src]');
    if (elements.length === 0) {
      console.log('No elements found for lazy loading.');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Fallback: immediately load background images if IntersectionObserver is not supported
      elements.forEach(el => {
        const src = el.getAttribute('data-src');
        if (src) {
          el.style.backgroundImage = `url(${src})`;
          el.classList.add('lazy-loaded');
        }
      });
      console.log(`Lazy loaded ${elements.length} elements without IntersectionObserver.`);
      return;
    }

    observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          const el = entry.target;
          const src = el.getAttribute('data-src');
          if (src) {
            el.style.backgroundImage = `url(${src})`;
            el.classList.add('lazy-loaded');
            observer.unobserve(el);
          }
        }
      });
    }, { rootMargin: '400px', threshold: 0 });

    elements.forEach(el => {
      observer.observe(el);
    });

    console.log(`Observing ${elements.length} elements for lazy background loading.`);
  } catch (error) {
    console.error('Error during lazy loading initialization:', error);
  }
}

afterUpdate(() => {
  initLazyLoad();
});
</script>
<section class="project-gallery">
  <div class="gallery-filters">
    <span class="filtertitle">Filter Images</span>
    <div class="filtertype">
      <div class="custom-select">
      <select bind:value={selectedStyle}>
        <option value="">All Styles</option>
        {#each styleOptions as style}
          <option value={style.value}>{style.label}</option>
        {/each}
      </select>
      </div>
    </div>
    <!-- <select bind:value={selectedColor}>
      <option value="">All Colors</option>
      {#each colorOptions as color}
        <option value={color}>{color}</option>
      {/each}
    </select> -->

    <div class="filtertype">
      <div class="custom-select">
      <select bind:value={selectedRoom}>
        <option value="">All Rooms</option>
        {#each roomOptions as room}
          <option value={room.value}>{room.label}</option>
        {/each}
      </select>
      </div>
    </div>

    <button type="button" class="clear-btn button clear" on:click={clearFilters} aria-label="Clear filters">Clear</button>
  </div>
  {#if allImages.length > 0}
    <div class="sub-links-wrap baguette-gallery">
      {#each allImages as img}
        <figure class="gallery-sub-link">
            <div class="gal-item-background" data-src="{img.src}"></div>
            <div class="overlay">
                <div class="iconswrap">
                  <div>
                    <a
                      class="icon-btn gallery-link"
                      href={img.link}
                      data-caption={img.caption}
                      title="Enlarge"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" version="1" viewBox="0 0 100 100">
                          <path d="M50 40a15 15 0 1 0 0 30 15 15 0 0 0 0-30zm40-15H78c-2 0-3-1-4-3l-3-9c-1-2-2-3-4-3H33c-2 0-3 1-4 3l-3 9c-1 2-2 3-4 3H10C5 25 0 30 0 35v45c0 6 5 10 10 10h80c6 0 10-4 10-10V35c0-5-4-10-10-10zM50 80a25 25 0 1 1 0-50 25 25 0 0 1 0 50zm37-38a3 3 0 1 1-1-7 3 3 0 0 1 1 7z" />
                      </svg>
                    </a>
                    </div>
                    {#if img.projectLink}
                      <a class="icon-btn project-link" href={img.projectLink} title="View Project">
                        <svg xmlns="http://www.w3.org/2000/svg" version="1" viewBox="0 0 512 512">
                          <path d="M506 179L360 33a17 17 0 0 0-26 0c-3 3-5 8-5 13v73h-64c-136 0-219 38-250 115-10 25-15 57-15 95a343 343 0 0 0 47 150c2 4 5 5 8 5s5-1 7-3l2-7-1-7v-7l-2-35c0-19 2-37 5-52s8-28 14-39c6-12 13-21 23-29a134 134 0 0 1 68-32 362 362 0 0 1 94-8h64v73c0 5 2 9 5 13 4 4 8 5 13 5s9-1 13-5l146-146c4-4 6-8 6-13s-2-9-6-13z" />
                        </svg>
                      </a>
                    {/if}
                </div>
            </div>
            <div class="square-overlay"><div class="border-left"></div><div class="border-right"></div></div>

        </figure>
      {/each}
    </div>
  {:else}
    <div class="noresults">
        <p>No Projects Match Your Filters. Please Try Again.</p>
    </div>
  {/if}
</section>

<style lang="scss">

  .sub-links-wrap { 
    /* gap: 1em; */
  }

    .gallery-filters { 
        background: #000;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        padding: 2rem 1rem;
    }
        .filtertype { 
        margin: 0 15px;
        min-width: 170px;
    }
        .filtertitle {
        text-transform: uppercase;
        color: #c2955b;
        font-size: 1.25rem;
     }
         .dropdown-toggle,
    select {
        cursor: pointer;
        background: #fff;
        border: 1px solid #ccc;
        padding: 1rem 1.3rem 1rem 1rem;
        line-height: 1;
        margin: 0;
        border: 0;
    }
        button.clear { 
        margin: 0 15px;
        padding: .8rem 2rem;
        border: 0;
        text-transform: uppercase; 
    }
  .filter-bar select {
      padding: 0.5rem;
      font-size: 1rem;
      border-radius: 6px;
      border: 1px solid #ccc;
    }
        .custom-select { 
        position: relative;
        width: 100%;
    }
    .custom-select:before { 
        content: url(data:image/svg+xml;charset=utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2211%22%20height%3D%227%22%3E%3Cpath%20fill%3D%22%23BB874C%22%20fill-rule%3D%22evenodd%22%20d%3D%22M10.9.5L5.5%206.9.1.5%22%2F%3E%3C%2Fsvg%3E);
        position: absolute;
        display: block;
        width: 11px;
        height: 7px;
        right: 15px;
        top: 12px;
    }
    .custom-select select { 
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        width: 100%;
    }

  figure {
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .gallery-sub-link { 
    width: 20em;
    flex-grow: 0;
    min-width: 20%;
    padding: 7em 1em;
    position: relative;
  }

  .gallery-sub-link::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0;
    z-index: 4;
    transition: opacity 0.3s ease;
  }
  .iconswrap { 
    position: relative;
    margin: 0px auto;
    display: flex;
    width: 100%;
    justify-content: center;
    z-index: 4;
    opacity: 0;
    transition: .3s ease all;
  }
  .icon-btn {
    position: relative;
    background: white;
    border: none;
    z-index: 3;
    font-size: 1.25rem;
    padding: 0.5rem;
    margin: 0 5px;
    width: 35px;
    height: 35px;
    cursor: pointer;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }
      .gallery-sub-link:hover .iconswrap {
        opacity: 1;
    }
    @media (max-width: 1250px) { 
        .gallery-filters { flex-wrap: wrap; }
        .filtertype { 
            flex-grow: 1;
        }
        .filtertitle { 
            width: 100%;
            text-align: center;
            margin-bottom: 1.5rem;
        }
    }
        @media (max-width: 920px) { 
        .gallery-filters { 
            justify-content: space-around;
        }
        .filtertype { 
            width: 48%;
            margin-bottom: 1rem;
            min-width: auto;
            margin: 5px;
        }
        button.clear { width: 100%; margin: 5px; }
    }

  .gal-item-background {
    width: 100%;
    padding-top: 75%;
    background-size: cover;
    background-position: center;
    opacity: 0;
    transition: opacity 0.3s ease-in;
    &.lazy-loaded { 
      opacity: 1!important;
    }
  }
</style>